import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  Search,
  Filter,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight,
} from 'lucide-react';

/**
 * RefundList - Component for viewing and filtering refunds
 *
 * Props:
 * - onSelectRefund: function - Called when a refund is selected for detail view
 * - isAdmin: boolean - Whether to show admin-level controls
 * - organizationId: string (optional) - Filter by organization (admin only)
 */
const RefundList = ({ onSelectRefund, isAdmin = false, organizationId = null }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refunds, setRefunds] = useState([]);
  const [total, setTotal] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);

  const statusConfig = {
    pending_approval: {
      label: 'Pending Approval',
      color: 'bg-yellow-100 text-yellow-800',
      icon: Clock,
    },
    approved: {
      label: 'First Approval',
      color: 'bg-blue-100 text-blue-800',
      icon: CheckCircle,
    },
    pending_second_approval: {
      label: 'Pending 2nd Approval',
      color: 'bg-orange-100 text-orange-800',
      icon: Clock,
    },
    processing: {
      label: 'Processing',
      color: 'bg-purple-100 text-purple-800',
      icon: ArrowRight,
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-800',
      icon: CheckCircle,
    },
    rejected: {
      label: 'Rejected',
      color: 'bg-red-100 text-red-800',
      icon: XCircle,
    },
    failed: {
      label: 'Failed',
      color: 'bg-red-100 text-red-800',
      icon: AlertCircle,
    },
  };

  const reasonLabels = {
    duplicate_charge: 'Duplicate Charge',
    service_not_provided: 'Service Not Provided',
    billing_error: 'Billing Error',
    customer_request: 'Customer Request',
    subscription_cancellation: 'Subscription Cancellation',
    other: 'Other',
  };

  useEffect(() => {
    loadRefunds();
  }, [statusFilter, offset]);

  const loadRefunds = async () => {
    try {
      setLoading(true);
      setError('');

      const endpoint = isAdmin
        ? `${getApiBase()}/admin/billing/refunds`
        : `${getApiBase()}/billing/refunds`;

      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', limit);
      params.append('offset', offset);
      if (organizationId) params.append('organization_id', organizationId);

      const response = await axios.get(
        `${endpoint}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      setRefunds(response.data.refunds || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading refunds:', err);
      setError(err.response?.data?.detail || 'Failed to load refunds');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter refunds by search term (client-side)
  const filteredRefunds = refunds.filter((refund) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      refund.id?.toLowerCase().includes(search) ||
      refund.requested_by_name?.toLowerCase().includes(search) ||
      refund.reason_notes?.toLowerCase().includes(search)
    );
  });

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || {
      label: status,
      color: 'bg-gray-100 text-gray-800',
      icon: AlertCircle,
    };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  if (loading && refunds.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Loading refunds...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search refunds..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setOffset(0);
              }}
              className="px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="pending_second_approval">Pending 2nd Approval</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={loadRefunds}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Refunds List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredRefunds.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No refunds found</p>
            {statusFilter && (
              <p className="text-sm mt-2">
                Try adjusting your filters
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Refund
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRefunds.map((refund) => (
                    <tr key={refund.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {refund.id?.substring(0, 8)}...
                          </p>
                          <p className="text-xs text-gray-500">
                            {refund.requested_by_name}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={refund.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(refund.amount, refund.currency)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {reasonLabels[refund.reason] || refund.reason}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {formatDate(refund.created_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => onSelectRefund && onSelectRefund(refund)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {offset + 1} - {Math.min(offset + limit, total)} of {total} refunds
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RefundList;
